import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ScoutingMatchCard from "./ScoutingMatchCard";

const startOfDay = (date) => {
  const nextDate = new Date(date);
  nextDate.setHours(0, 0, 0, 0);
  return nextDate;
};

const parseMatchDate = (match) => {
  const rawDate = match.fechaISO || match.fecha;
  if (!rawDate) return null;

  const parsedDate = new Date(rawDate);
  if (Number.isNaN(parsedDate.getTime())) return null;

  const timeValue = typeof match.hora === "string" ? match.hora.trim() : "";
  const [hours, minutes] = timeValue.split(":").map(Number);

  if (Number.isFinite(hours) && Number.isFinite(minutes)) {
    parsedDate.setHours(hours, minutes, 0, 0);
  }

  return parsedDate;
};

const formatDayKey = (date) => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
);

const formatDayLabel = (date, locale) => (
  date.toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
  })
);

/**
 * ScoutingAgendaView — Upcoming agenda grouped by day and sorted by kickoff time.
 *
 * Props:
 *  - matches {Array} watchedMatch documents
 *  - onUnwatch {Function} called with partidoId
 */
const ScoutingAgendaView = ({ matches = [], onUnwatch }) => {
  const { t, i18n } = useTranslation("scouting");
  const agendaGroups = useMemo(() => {
    const today = startOfDay(new Date());

    const normalizedMatches = matches
      .map((match, index) => {
        const matchDate = parseMatchDate(match);
        const dayStart = matchDate ? startOfDay(matchDate) : null;

        return {
          key: match.partidoId || match.id || `${match.equipoLocal}-${match.equipoVisitante}-${index}`,
          label: matchDate ? formatDayLabel(matchDate, i18n.language) : t("components.summaryCards.dateUnknown"),
          dayKey: matchDate ? formatDayKey(matchDate) : `sin-fecha-${index}`,
          sortValue: matchDate ? matchDate.getTime() : Number.MAX_SAFE_INTEGER,
          dayStart,
          match,
        };
      })
      .sort((left, right) => left.sortValue - right.sortValue);

    const visibleMatches = normalizedMatches.filter(({ dayStart }) => !dayStart || dayStart >= today);

    return visibleMatches.reduce((accumulator, item) => {
      const existingGroup = accumulator.find((group) => group.dayKey === item.dayKey);

      if (existingGroup) {
        existingGroup.matches.push(item.match);
        return accumulator;
      }

      accumulator.push({
        dayKey: item.dayKey,
        label: item.label,
        matches: [item.match],
      });
      return accumulator;
    }, []);
  }, [matches, i18n.language, t]);

  if (matches.length === 0) {
    return (
      <Box className="sc-empty">
        <Typography component="h3">
          {t("myAgenda.empty")}
        </Typography>
        <Typography sx={{ mt: 1 }}>
          {t("myAgenda.emptyDesc")}
        </Typography>
      </Box>
    );
  }

  if (agendaGroups.length === 0) {
    return (
      <Box className="sc-empty">
        <Typography component="h3">
          {t("workspace.noAgendaDays")}
        </Typography>
        <Typography sx={{ mt: 1 }}>
          {t("workspace.agendaDescription")}
        </Typography>
      </Box>
    );
  }

  const visibleMatchCount = agendaGroups.reduce((total, group) => total + group.matches.length, 0);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box className="sc-agenda-heading">
        <Typography component="h3">{t("workspace.nextDays")}</Typography>
        <Typography className="sc-muted">
          {t("workspace.agendaSummary", { matches: visibleMatchCount, days: agendaGroups.length })}
        </Typography>
      </Box>

      <Box className="sc-agenda-days">
        {agendaGroups.map((group) => (
          <Box
            component="section"
            className="sc-day"
            key={group.dayKey}
          >
            <Box className="sc-day-heading">
              <Typography component="h3">
                {group.label}
              </Typography>
              <span>{t("components.summaryCards.matches", { count: group.matches.length })}</span>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {group.matches.map((match) => (
                  <ScoutingMatchCard
                    key={match.partidoId || match.id}
                    match={match}
                    isWatched
                    onToggle={() => onUnwatch(match.partidoId)}
                  />
                ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ScoutingAgendaView;
